<script setup lang="ts">
import type { CollectionType } from "~/types";
import { useAuthStore } from "~/stores/auth/store";
import { v4 as uuidv4 } from "uuid";
import { repository } from "~/repository";
import { acceptedCategory, acceptedService } from "vanilla-cookieconsent";

declare global {
  interface Window {
    jwplayer: any;
  }
}

useHead({
  script: [
    { src: "https://cdn.jwplayer.com/libraries/8FnajRft.js", async: true },
  ],
});

const route = useRoute();

const videoSessionID = uuidv4();

const { $CC } = useNuxtApp();

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const props = defineProps<{
  source: { file: string }[] | string;
  duration: number;
  progress: number;
  id: string;
  videoType: CollectionType;
  trackProgress: boolean;
}>();

const onSetupErrorRetryAttempts = ref(0);
const onErrorRetryAttempts = ref(0);

const videoPlayerRef = ref(null);
const player = ref<any>(null);

const patchProgressInterval = ref<any>(null);
const intervalArr = ref<any>([]);

const initialPlayingPosition = ref<string>("unset");

function initJWPlayer(
  autostart: boolean,
  playlist: { file: string }[] | string,
) {
  console.log($CC.acceptedCategory("analytics"));
  if (window.jwplayer) {
    if (player.value) {
      player.value.remove();
    }

    player.value = window.jwplayer(videoPlayerRef.value);
    player.value.setup({
      playlist,
      playbackRateControls: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
      autostart: autostart,
      playlistIndex: 0,
      repeat: false,
      doNotSaveCookies: !$CC.acceptedCategory("analytics"),
    });

    if (props.trackProgress) {
      player.value.on("play", onPlayerPlay);
      player.value.on("pause", onPlayerPause);
      player.value.on("seeked", onSeeked);
      player.value.on("complete", clearTrackingInterval);
      player.value.on("setupError", onSetupError);
      player.value.on("error", onError);
    }
  } else {
    console.log("window.jwplayer not available");
  }
}

onMounted(() => {
  const interval = setInterval(() => {
    if (window.jwplayer) {
      clearInterval(interval);
      initJWPlayer(true, props.source);
    }
  }, 100);
});

watch(
  () => props.progress,
  () => {
    if (props.videoType !== "vod") {
      return;
    }
    if (!player.value) return;

    const playerPosition = player.value.getPosition();

    const maxProgress = Math.max(...[playerPosition, props.progress]);

    if (initialPlayingPosition.value === "unset") {
      player.value.seek(maxProgress);
      initialPlayingPosition.value = "set";
      postProgressEvent("video:load");
    }
  },
);

function onSeeked() {
  postProgressEvent("video:scrub");
}

async function onError() {
  console.log("player onError");
  if (onErrorRetryAttempts.value < 3) {
    if (props.videoType === "live") {
      const { data } = await repository.videos.getVideoById({
        videoId: Array.isArray(route.params.slug)
          ? route.params.slug.join("")
          : route.params.slug || "",
      });

      initJWPlayer(false, [{ file: data.source }]);
    } else {
      initJWPlayer(true, props.source);
    }
    onErrorRetryAttempts.value++;
  }
}

function onSetupError(event: any) {
  console.log("player onSetupError", event);
  if (onSetupErrorRetryAttempts.value < 3) {
    onSetupErrorRetryAttempts.value++;
    initJWPlayer(true, props.source);
  }
}

function onPlayerPlay() {
  if (!player.value) return;

  if (patchProgressInterval.value === null) {
    patchProgressInterval.value = setInterval(() => {
      postProgressEvent("video:play");
    }, 6000);
    intervalArr.value.push(patchProgressInterval.value);
  }
}

function onPlayerPause() {
  clearTrackingInterval();

  if (!player.value) return;

  postProgressEvent("video:stop");
}

function postProgressEvent(action: string) {
  if (!player.value) return;

  let currentProgress = player.value.getPosition();
  if (currentProgress > props.duration && props.duration > 0) {
    currentProgress = props.duration;
  }

  let elapsed = 0;
  if (action === "video:scrub" && currentProgress < 6) {
    return;
  } else if (action === "video:play") {
    elapsed = 6;
  } else if (action === "video:load") {
    elapsed = 0;
  } else {
    elapsed = currentProgress;
  }

  repository.videos.postEvent({
    subscriber_id: user.value.subscriberID,
    video_session_id: videoSessionID,
    action: action,
    content_id: props.id,
    elapsed: elapsed,
    content_type: props.videoType,
    platform: "web",
    player_position: currentProgress,
    application_version: "2.5.0",
  });
}

function clearTrackingInterval() {
  intervalArr.value.forEach((item: any) => {
    clearInterval(item);
  });
  patchProgressInterval.value = null;
}

function cleanupPlayer() {
  clearTrackingInterval();
  player.value.remove();
}

defineExpose({
  cleanupPlayer,
});
</script>

<template>
  <div ref="videoPlayerRef" class="min-h-[612px] w-full" />
</template>

<style scoped></style>
